import polyfill from "../packages/excalidraw/polyfill";
import LanguageDetector from "i18next-browser-languagedetector";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trackEvent } from "../packages/excalidraw/analytics";
import { ErrorDialog } from "../packages/excalidraw/components/ErrorDialog";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import {
  APP_NAME,
  EVENT,
  THEME,
  TITLE_TIMEOUT,
  VERSION_TIMEOUT,
} from "../packages/excalidraw/constants";
import { loadFromBlob } from "../packages/excalidraw/data/blob";
import {
  ExcalidrawElement,
  FileId,
  NonDeletedExcalidrawElement,
  Theme,
} from "../packages/excalidraw/element/types";
import { useCallbackRefState } from "../packages/excalidraw/hooks/useCallbackRefState";
import { t } from "../packages/excalidraw/i18n";
import {
  Excalidraw,
  defaultLang,
  LiveCollaborationTrigger,
  TTDDialog,
  TTDDialogTrigger,
  ExtraToolsSlot,
} from "../packages/excalidraw/index";
import {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "../packages/excalidraw/types";
import {
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  ResolvablePromise,
  resolvablePromise,
  isRunningInIframe,
} from "../packages/excalidraw/utils";
import {
  R2_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  CollabAPI,
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import {
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";
import CustomStats from "./CustomStats";
import {
  restore,
  restoreAppState,
  RestoredDataState,
} from "../packages/excalidraw/data/restore";
// import {
//   ExportToExcalidrawPlus,
//   exportToExcalidrawPlus,
// } from "./components/ExportToExcalidrawPlus";
import { updateStaleImageStatuses } from "./data/FileManager";
import { newElementWith } from "../packages/excalidraw/element/mutateElement";
import { isInitializedImageElement } from "../packages/excalidraw/element/typeChecks";
import { fitLoadedScene } from "./data/fitLoadedScene";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import clsx from "clsx";
import { reconcileElements } from "./collab/reconciliation";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "../packages/excalidraw/data/library";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { AgentsSidebar } from "./components/agents/AgentsSidebar";
import { AgentsToolbarItem } from "./components/agents/AgentsToolbarItem";
import { AppFooter } from "./components/AppFooter";
import { atom, Provider, useAtom, useAtomValue } from "jotai";
import { useAtomWithInitialValue } from "../packages/excalidraw/jotai";
import { appJotaiStore } from "./app-jotai";

import "./index.scss";
import { ResolutionType } from "../packages/excalidraw/utility-types";
import { openConfirmModal } from "../packages/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { OverwriteConfirmDialog } from "../packages/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import Trans from "../packages/excalidraw/components/Trans";
import { CollabDialog, collabDialogOpenAtom } from "./collab/CollabDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import OAuthIntegration from "./components/canvas/OAuthIntegration";
import CommandPalette from "./components/canvas/CommandPalette";
import { RenderIntegration } from "./components/canvas/renderers/RenderIntegration";
import NotionSidePeek from "./components/canvas/NotionSidePeek";
import FeedbackModal, {
  isLegacyFeedbackModalOpen,
} from "./components/canvas/FeedbackModal";
import { loadFilesFromR2 } from "./data/r2AWSStorage";
import { useAuth } from "./hooks/useAuth";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  getCloudFileIdFromUrl,
  fileEditorPath,
  hasNewMindmapRequest,
} from "./data/cloudRouting";
import {
  loadSceneImagesFromCloud,
  shouldRetryCloudImages,
  PendingCloudImages,
} from "./data/cloudScene";
import { loadCloudEditorFile } from "./data/cloudEditorFile";
import {
  isRecognizedSceneNavigation,
  matchLegacySceneHash,
  matchExternalSceneHash,
} from "./data/sceneHashNavigation";
import { generateMindmapFromPage } from "./data/mindmapGenerate";
import { refreshMindmapFromNotion } from "./data/mindmapRefresh";
import { regroupMindmap } from "./data/mindmapRegroup";
import {
  toggleMindmapExpand,
  expandAllMindmap,
  repairMindmapExpandFlags,
} from "./data/mindmapExpand";
import { setMindmapApi } from "./data/mindmapApiRef";
import { createMindmapDeletionRecorder } from "./data/mindmapElements";
import { clearMindmapDeletionLedger } from "../packages/mindmap-core/src/mindmapElements";
import {
  mindmapNodeSignature,
  rerouteMindmapConnectors,
} from "./data/mindmapReroute";
import {
  subscribeEvent,
  unSubscribeEvent,
} from "../packages/excalidraw/events";
import {
  clearCloudFileBuffer,
  loadCloudFileBuffer,
} from "./data/cloudFileBuffer";
import {
  getFile,
  renameFile,
  setVisibility,
  FileRecord,
  FileVisibility,
} from "./services/filesService";
import ShareControl, { SharePopoverWithEmbed } from "./components/ShareControl";
import FileTitleChip from "./components/FileTitleChip";
import SaveStatusChip from "./components/SaveStatusChip";
import { VersionConflictBanner } from "./components/VersionConflictBanner";
import SaveWorkPrompt from "./components/SaveWorkPrompt";
import CloudFile404 from "./components/CloudFile404";
import SaveToCloudButton from "./components/SaveToCloudButton";
import NewMindmapConfirmDialog from "./components/NewMindmapConfirmDialog";
import SaveToCloudDialog from "./share/SaveToCloudDialog";
import { useCloudAutosave } from "./hooks/useCloudAutosave";
import {
  decideNewMindmap,
  unsavedWorkKind,
  UnsavedWorkKind,
} from "./data/unsavedWork";
import {
  cloudLoadAction,
  shouldEnterCreateOnFirstEdit,
  shouldHydrateCloudFile,
  shouldOfferSaveWorkPrompt,
  shouldRestoreBrowserScene,
} from "./data/editorGuards";
// import { useSidePeek } from "./hooks/useSidePeek";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const languageDetector = new LanguageDetector();
languageDetector.init({
  languageUtils: {},
});

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = matchLegacySceneHash(window.location.hash);
  const externalUrlMatch = matchExternalSceneHash(window.location.hash);

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted(),
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const detectedLangCode = languageDetector.detect() || defaultLang.code;
export const appLangCodeAtom = atom(
  Array.isArray(detectedLangCode) ? detectedLangCode[0] : detectedLangCode,
);

// Toast function atom for showing notifications across the app
export const toastFunctionAtom = atom<((message: string) => void) | null>(null);

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [langCode, setLangCode] = useAtom(appLangCodeAtom);
  const isCollabDisabled = isRunningInIframe();
  const [showBanner, setShowBanner] = useState(false);
  const { session, authResolved } = useAuth();
  // const { setShow } = useSidePeek();

  // Cloud file ("My Files") state. `cloudFileId` is derived from the `/files/<id>`
  // URL; null on the scratch canvas (`/`) and on any `#room=` URL, which is a pure
  // collab session that keeps the whole cloud subsystem dormant. Bumping this
  // state forces a re-read of the URL after an in-app (pushState) navigation.
  const [, setLocationTick] = useState(0);
  const cloudFileId = getCloudFileIdFromUrl();
  const [cloudFile, setCloudFile] = useState<FileRecord | null>(null);
  const [cloudNotFound, setCloudNotFound] = useState(false);
  // A transient (retryable) failure loading the cloud file — distinct from the
  // 404, which would lie to the owner about a server blip.
  const [cloudError, setCloudError] = useState(false);
  // Bumped by the retry button to re-run the hydration effect after an error.
  const [hydrationRetry, setHydrationRetry] = useState(0);
  // True once a cloud file's scene has actually been applied to the editor.
  // Gates the loading overlay so /f/<id> never flashes the empty board first.
  const [cloudSceneReady, setCloudSceneReady] = useState(false);
  const [isSaveToCloudOpen, setIsSaveToCloudOpen] = useState(false);
  // Mobile share entry point: renderTopRightUI (and its anchored Share button)
  // doesn't render on mobile, so the ☰ menu opens the share popover as a
  // centered modal instead. Desktop keeps the anchored button.
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Login handoff: offer to save a non-empty scratch canvas once, per session.
  const [showSaveWorkPrompt, setShowSaveWorkPrompt] = useState(false);
  const saveWorkPromptDismissedRef = useRef(false);
  // Does the canvas hold anything at all? Drives both the top-right Save button
  // and the "you have unsaved work" guard in front of New mindmap.
  const [hasSceneContent, setHasSceneContent] = useState(false);
  // Set while the New-mindmap confirmation is up (see data/unsavedWork).
  const [newMindmapPrompt, setNewMindmapPrompt] =
    useState<UnsavedWorkKind | null>(null);
  // "Save it first" from that confirmation: once the save lands, go straight on
  // to the new mindmap instead of opening the file we just saved.
  const startNewAfterSaveRef = useRef(false);
  // Id of a file whose scene is ALREADY the editor's scene — it was just created
  // by create-on-first-edit, or just written by an explicit save-to-cloud. The
  // load effect must adopt it instead of re-fetching and repainting what is
  // already on screen (a full canvas reload right after saving), which could
  // also overwrite strokes drawn while the row was being written.
  const sceneAlreadyInEditorRef = useRef<string | null>(null);
  // One-shot: did this document load with a `/?new=1` request from My Files?
  // Read once at mount so scrubbing the URL can't hide it from the effect.
  const newMindmapRequestRef = useRef(hasNewMindmapRequest());
  // Id of the cloud file whose scene we've already hydrated into the editor.
  // Guards the hydration effect (which re-runs on every token refresh) from
  // re-fetching and overwriting live edits — hydrate at most once per id.
  const hydratedFileIdRef = useRef<string | null>(null);
  // Set by autosave's silent reload (agent commit on a clean tab): the next
  // hydration must not put the loading overlay over a scene the user can keep
  // looking at. Consumed by the hydration effect.
  const silentReloadRef = useRef(false);
  // Mirrors `isCloudContext` for reads inside the mount-once sync effect below,
  // whose closure would otherwise be stale. The multi-tab scratch-scene restore
  // must never fire while a cloud file is open.
  const isCloudContextRef = useRef(false);

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  const [, setCollabDialogOpen] = useAtom(collabDialogOpenAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);
  const [, setToastFunction] = useAtom(toastFunctionAtom);

  // Set toast function when excalidrawAPI becomes available
  useEffect(() => {
    if (excalidrawAPI) {
      setToastFunction(() => (message: string) => {
        excalidrawAPI.setToast({ message });
      });
    }
  }, [excalidrawAPI, setToastFunction]);

  // Share the imperative API with the Notion picker's "Generate mindmap" action
  // (issue #45). The picker renders deep inside the core component and only gets
  // render + appState, so it reads this ref to reach the full API.
  useEffect(() => {
    setMindmapApi(excalidrawAPI ?? null);
    return () => setMindmapApi(null);
  }, [excalidrawAPI]);

  // "Refresh from Notion" (issue #46). The context-menu action lives in the core
  // package, which can't reach the Notion fetch layer, so it dispatches a canvas
  // event; the shell runs the diff + apply against the live scene here.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const handler = (event: CustomEvent) => {
      const { mapId, rootPageId } = event.detail ?? {};
      if (mapId && rootPageId) {
        // Fire-and-forget, so a throw inside the refresh pipeline would reject
        // silently and the menu item would just never respond.
        refreshMindmapFromNotion(excalidrawAPI, mapId, rootPageId).catch(() =>
          excalidrawAPI.setToast({ message: t("mindmap.refreshUnreachable") }),
        );
      }
    };
    subscribeEvent("refreshMindmapFromNotion", handler);
    return () => unSubscribeEvent("refreshMindmapFromNotion", handler);
  }, [excalidrawAPI]);

  // "Group by" on a database map's root pill. `regroupMindmap` owns the whole
  // thing — refetch, rebuild the tree, lay it out and commit ONCE, so one user
  // decision is one undo step (the old restamp-then-refresh wiring cost two).
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const handler = (event: CustomEvent) => {
      const { mapId, rootPageId, groupingProperty } = event.detail ?? {};
      if (!mapId || !rootPageId) {
        return;
      }
      // Fire-and-forget: an unhandled rejection here would leave the menu item
      // looking dead.
      regroupMindmap(excalidrawAPI, mapId, rootPageId, groupingProperty).catch(
        () =>
          excalidrawAPI.setToast({ message: t("mindmap.refreshUnreachable") }),
      );
    };
    subscribeEvent("mindmapRegroup", handler);
    return () => unSubscribeEvent("mindmapRegroup", handler);
  }, [excalidrawAPI]);

  // "Restore removed nodes": forget a map's deletion ledger, then refresh so the
  // forgotten nodes come back from Notion. They are re-placed by the layout —
  // their old positions died with the deletion and are not recoverable.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const handler = (event: CustomEvent) => {
      const { mapId, rootPageId } = event.detail ?? {};
      if (!mapId || !rootPageId) {
        return;
      }
      const cleared = clearMindmapDeletionLedger(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        mapId,
      );
      if (cleared) {
        // Committed: forgetting the deletions is itself an undoable user action.
        excalidrawAPI.updateScene({
          elements: cleared,
          commitToHistory: true,
        });
      }
      refreshMindmapFromNotion(excalidrawAPI, mapId, rootPageId).catch(() =>
        excalidrawAPI.setToast({ message: t("mindmap.refreshUnreachable") }),
      );
    };
    subscribeEvent("mindmapRestoreDeleted", handler);
    return () => unSubscribeEvent("mindmapRestoreDeleted", handler);
  }, [excalidrawAPI]);

  // Mindmap expand/collapse (issue #51). A node's chip dispatches
  // `mindmapToggleExpand`; "Expand all" dispatches `mindmapExpandAll`. Both need
  // the Notion fetch layer + imperative API, so the shell runs them here against
  // the live scene, mirroring the refresh wiring above.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const onToggle = (event: CustomEvent) => {
      const { elementId } = event.detail ?? {};
      if (elementId) {
        // Fire-and-forget: without a catch a throw inside the expand pipeline
        // (a cyclic recordMap used to blow the stack) rejected silently and the
        // chip just never responded.
        toggleMindmapExpand(excalidrawAPI, elementId).catch(() =>
          excalidrawAPI.setToast({ message: t("mindmap.expandFailed") }),
        );
      }
    };
    const onExpandAll = (event: CustomEvent) => {
      const { elementId } = event.detail ?? {};
      if (elementId) {
        expandAllMindmap(excalidrawAPI, elementId).catch(() =>
          excalidrawAPI.setToast({ message: t("mindmap.expandFailed") }),
        );
      }
    };
    subscribeEvent("mindmapToggleExpand", onToggle);
    subscribeEvent("mindmapExpandAll", onExpandAll);
    return () => {
      unSubscribeEvent("mindmapToggleExpand", onToggle);
      unSubscribeEvent("mindmapExpandAll", onExpandAll);
    };
  }, [excalidrawAPI]);

  // Connector upkeep (V3). Mindmap connectors are natively BOUND to their two
  // pills, so a dragged pill drags its arrows live — but Excalidraw's binding
  // only moves the arrow's end points, leaving the elbow bent. Restore the
  // canonical route once the gesture ENDS, from a document pointerup rather than
  // Excalidraw's `onChange`: an unconditional updateScene from onChange is
  // exactly the pattern that produced React #185. The rAF defers past both
  // Excalidraw's own pointerup (which writes the drag's history entry) and the
  // binding update it triggers, so we rewrite the FINAL bound geometry rather
  // than racing it; ours then rides along with commitToHistory: false so one
  // undo reverts the move and its routing together. The signature plus the
  // helper's null return mean an idle click commits nothing at all.
  //
  // The same pass carries the migration for maps generated before connectors
  // were bound: `rerouteMindmapConnectors` binds first, so a legacy scene is
  // repaired by the first gesture (and by the mount pass below). It also
  // brings each chip's stored flags in line with the canvas
  // (`repairMindmapExpandFlags`): a map written before parents were emitted
  // expanded showed a "show children" chevron over children already visible.
  const lastMindmapSignature = useRef<string>("");
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    let frame = 0;
    const syncConnectors = (commitToHistory: boolean) => {
      const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
      const signature = mindmapNodeSignature(elements);
      if (signature === lastMindmapSignature.current) {
        return;
      }
      lastMindmapSignature.current = signature;
      const rerouted = rerouteMindmapConnectors(elements);
      const next = repairMindmapExpandFlags(rerouted ?? elements) ?? rerouted;
      if (next) {
        excalidrawAPI.updateScene({ elements: next, commitToHistory });
      }
    };
    const onPointerUp = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => syncConnectors(false));
    };
    // A scene loaded from localStorage / the cloud / a share link may predate
    // binding entirely; bind it on arrival instead of waiting for a first click,
    // and off-history so the repair is not an undo step of the user's.
    frame = requestAnimationFrame(() => syncConnectors(false));
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointerup", onPointerUp);
      cancelAnimationFrame(frame);
    };
  }, [excalidrawAPI]);

  // Dev-only console entry for the tracer (issue #44):
  //   __generateMindmap("<notion-page-id>")
  useEffect(() => {
    if (!import.meta.env.DEV || !excalidrawAPI) {
      return;
    }
    (window as any).__generateMindmap = (pageId: string) =>
      generateMindmapFromPage(excalidrawAPI, pageId);
    return () => {
      delete (window as any).__generateMindmap;
    };
  }, [excalidrawAPI]);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromR2({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromR2(
            `${R2_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    if (getCloudFileIdFromUrl()) {
      // Cloud file: don't hydrate the scratch canvas from localStorage. The
      // dedicated cloud-load effect applies the scene once it (and the session)
      // are ready.
      initialStatePromiseRef.current.promise.resolve(null);
    } else {
      initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
        loadImages(data, /* isInitialLoad */ true);
        initialStatePromiseRef.current.promise.resolve(data.scene);
      });
    }

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (
        !libraryUrlTokens &&
        isRecognizedSceneNavigation(event.oldURL, event.newURL)
      ) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              commitToHistory: true,
            });
          }
        });
      }
    };

    const titleTimeout = setTimeout(
      () => (document.title = APP_NAME),
      TITLE_TIMEOUT,
    );

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state.
        // NEVER restore the anonymous scratch scene while a cloud file is open:
        // that scratch localStorage belongs to `/`, not to this file, and
        // splatting it over the editor (then autosaving it) corrupts the file.
        if (
          shouldRestoreBrowserScene({
            isCloudContext: isCloudContextRef.current,
            browserStateNewer: isBrowserStorageStateNewer(
              STORAGE_KEYS.VERSION_DATA_STATE,
            ),
          })
        ) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          let langCode = languageDetector.detect() || defaultLang.code;
          if (Array.isArray(langCode)) {
            langCode = langCode[0];
          }
          setLangCode(langCode);
          excalidrawAPI.updateScene({
            ...localDataState,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
      clearTimeout(titleTimeout);
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  // Point the URL at a cloud file, then re-render so `cloudFileId` (read from
  // the pathname) picks up the new id. Replace, not push: `/` is the same canvas
  // gaining an id, and its scratch scene is cleared here, so a pushed `/` entry
  // would send Back to a blank canvas.
  const navigateToCloudFile = useCallback((fileId: string) => {
    window.history.replaceState({}, "", fileEditorPath(fileId));
    setLocationTick((tick) => tick + 1);
  }, []);

  // In-app navigation rewrites the URL directly (above) and fires no event, so
  // browser Back/Forward would change it without re-deriving `cloudFileId` —
  // leaving the editor pointed at the wrong file (autosave still PUTting to it).
  // Observe popstate and bump the tick so the pathname is re-read.
  useEffect(() => {
    const onPopState = () => setLocationTick((tick) => tick + 1);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Point the editor at a file whose scene is already on screen (create-on-
  // first-edit, or a confirmed save-to-cloud): flag it so the loader adopts
  // rather than re-hydrates, then switch the URL to /files/<id>.
  const adoptFileAlreadyInEditor = useCallback(
    (fileId: string) => {
      sceneAlreadyInEditorRef.current = fileId;
      navigateToCloudFile(fileId);
    },
    [navigateToCloudFile],
  );

  // Create-on-first-edit just materialised the row. Until this moment the scene
  // was mirrored into the scratch keys as its only safety net (see onChange);
  // the file owns it now, so drop the duplicate or `/` keeps showing it and then
  // diverges from the file. `clearScratchScene` cancels the pending debounced
  // save first, so nothing rewrites the keys behind us.
  const adoptCreatedFile = useCallback(
    (fileId: string) => {
      LocalData.clearScratchScene();
      adoptFileAlreadyInEditor(fileId);
    },
    [adoptFileAlreadyInEditor],
  );

  // Autosave + file-lifecycle engine for cloud files. Owns debounced saving,
  // create-on-first-edit, the offline buffer, and the status-chip state.
  const cloudAutosave = useCloudAutosave({
    excalidrawAPI,
    accessToken: session?.access_token,
    fileIdFromPath: cloudFileId,
    loadedVersion: cloudFile?.version ?? null,
    onCreated: adoptCreatedFile,
    // In a live session the collab upload path owns the image "saved" stamp.
    isCollaborating: () => collabAPI?.isCollaborating() ?? false,
    // Re-run the hydration effect: forgetting the hydrated id is what makes it
    // fetch and apply the server copy again instead of bailing as already-open.
    onReloadFromCloud: (options) => {
      hydratedFileIdRef.current = null;
      silentReloadRef.current = options?.silent === true;
      setHydrationRetry((tick) => tick + 1);
    },
  });
  // Stable references (the hook returns a fresh object each render, so the whole
  // object must never be an effect/callback dependency).
  const cloudMarkSynced = cloudAutosave.markSynced;
  const cloudMarkHydrated = cloudAutosave.markHydrated;
  const cloudFlushNow = cloudAutosave.flushNow;
  const cloudSaveNow = cloudAutosave.saveNow;
  const cloudStartNewMindmap = cloudAutosave.startNewMindmap;
  const cloudHandleChange = cloudAutosave.handleChange;
  const isCloudContext = cloudAutosave.isCloudContext;
  const isPreCreate = cloudAutosave.isPreCreate;
  const cloudHasUnsavedChanges = cloudAutosave.hasUnsavedChanges;
  const cloudFlushBeforeUnload = cloudAutosave.flushBeforeUnload;
  // Keep the ref the mount-once sync effect reads in step with render state.
  isCloudContextRef.current = isCloudContext;

  // Cloud image assets that failed to load, kept so a fresh token can retry.
  const pendingCloudImagesRef = useRef<PendingCloudImages | null>(null);

  /**
   * Load a cloud file's image assets. Failures are handled, not swallowed: the
   * affected elements get the "not found" state (as every other image path does
   * via updateStaleImageStatuses — this one didn't, so a failed asset rendered as
   * a silently blank image), and the failed ids are retained for a token-change
   * retry.
   */
  const loadCloudImages = useCallback(
    async (
      api: ExcalidrawImperativeAPI,
      fileId: string,
      fileIds: readonly FileId[],
      token: string | undefined,
      isCancelled: () => boolean,
    ) => {
      const { loadedFiles, erroredFiles } = await loadSceneImagesFromCloud(
        fileId,
        fileIds,
        token,
      );
      if (isCancelled()) {
        return;
      }
      if (loadedFiles.length) {
        api.addFiles(loadedFiles);
      }
      updateStaleImageStatuses({
        excalidrawAPI: api,
        erroredFiles,
        elements: api.getSceneElementsIncludingDeleted(),
      });
      pendingCloudImagesRef.current = erroredFiles.size
        ? { fileId, fileIds: [...erroredFiles.keys()], token }
        : null;
    },
    [],
  );

  // Hydrate the editor from a cloud file when on /f/<id>. Re-runs when the
  // session (access token) resolves so an owner opening their private file
  // loads once auth is available.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    if (!cloudFileId) {
      // Left the file (scratch canvas / browser Back). Forget the hydrated id so
      // returning to this file later re-hydrates it.
      if (hydratedFileIdRef.current !== null) {
        // A cloud file was on screen. Without swapping the editor back to the
        // scratch scene, the file's content keeps rendering on `/` and the
        // next onChange (now routed to LocalData) would overwrite the
        // anonymous scratch keys with it.
        excalidrawAPI.updateScene({ ...importFromLocalStorage() });
        setCloudFile(null);
        setCloudNotFound(false);
        setCloudError(false);
      }
      hydratedFileIdRef.current = null;
      return;
    }

    // Already hydrated this id: a token refresh (or auth re-resolution) re-ran
    // this effect. The scene is live in the editor; re-fetching and re-applying
    // the server copy would overwrite the user's edits, so bail.
    if (
      !shouldHydrateCloudFile({
        currentFileId: cloudFileId,
        hydratedFileId: hydratedFileIdRef.current,
      })
    ) {
      return;
    }

    // Created or saved from here: the scene on screen IS this file's scene. Only
    // fetch metadata (for the title chip) — re-fetching and re-applying the
    // scene we just wrote is a visible canvas reload for no gain.
    if (sceneAlreadyInEditorRef.current === cloudFileId) {
      sceneAlreadyInEditorRef.current = null;
      // Adopting a file resets the autosave baseline, so re-establish it from
      // the on-screen scene: it matches the cloud, and without this the very
      // first onChange echo would PUT it straight back.
      cloudMarkSynced(
        excalidrawAPI.getSceneElementsIncludingDeleted(),
        excalidrawAPI.getAppState(),
      );
      // Nothing to load — the editor already holds this file's scene, so
      // autosave may proceed.
      cloudMarkHydrated();
      hydratedFileIdRef.current = cloudFileId;
      setCloudNotFound(false);
      setCloudSceneReady(true);
      let cancelledMeta = false;
      getFile(cloudFileId)
        .then((meta) => !cancelledMeta && setCloudFile(meta))
        .catch(() => undefined);
      return () => {
        cancelledMeta = true;
      };
    }

    let cancelled = false;
    let stopFitting: (() => void) | null = null;
    // Fresh load for a not-yet-hydrated id: hold the loading overlay up (below)
    // until the scene is applied, so the empty board never flashes first. A
    // silent reload (an agent committed while this tab was clean) keeps the
    // current scene on screen instead — the fresh one simply replaces it.
    const isSilentReload = silentReloadRef.current;
    if (!isSilentReload) {
      setCloudSceneReady(false);
    }
    silentReloadRef.current = false;
    setCloudError(false);
    (async () => {
      // `/f/<id>` is owner-only: the loader fetches metadata first and gates on
      // ownership BEFORE requesting the scene, so a non-owner (even of a PUBLIC
      // file) resolves to the uniform 404 with no scene to hydrate and no
      // autosave/buffer ever arming. Metadata is reused for the title chip.
      const { status, file, scene } = await loadCloudEditorFile(
        cloudFileId,
        session?.user?.id,
        session?.access_token,
      );
      if (cancelled) {
        return;
      }
      const action = cloudLoadAction(status);
      if (action === "notFound") {
        setCloudNotFound(true);
        return;
      }
      if (action === "retryableError" || !scene) {
        // Transient failure: surface a retryable error (NOT the 404) and leave
        // `hydratedFileIdRef` unset so a retry / re-focus re-attempts the load.
        setCloudError(true);
        return;
      }
      // `ready` or `empty` (self-healing orphan) — both carry a scene to apply.
      setCloudNotFound(false);
      setCloudError(false);
      setCloudFile(file);

      // Prefer unsynced offline edits from a previous session: a per-file buffer
      // only exists when the last save didn't reach the cloud, so it is normally
      // fresher than the server copy. Restoring it (and re-arming autosave)
      // re-syncs it.
      //
      // But NEVER let an empty buffer replace a scene that has content. A buffer
      // is written on every failed save, so a spell of failing saves (a bad
      // deploy, an expired session, an outage) leaves an empty buffer behind for
      // any file that was open on an empty canvas. Restoring that buffer wipes
      // the canvas, and because a restored buffer is deliberately not marked
      // synced, autosave then flushes the emptiness to the server and destroys
      // the user's work permanently. An empty buffer carries no information;
      // when the server has content, the server wins.
      const rawBuffer = loadCloudFileBuffer(cloudFileId);
      const serverHasContent = (scene.elements?.length ?? 0) > 0;
      const bufferHasContent = (rawBuffer?.elements?.length ?? 0) > 0;
      const buffered =
        rawBuffer && !bufferHasContent && serverHasContent ? null : rawBuffer;

      if (rawBuffer && !buffered) {
        // Discard the useless empty buffer so it can't shadow this file again.
        clearCloudFileBuffer(cloudFileId);
      }

      const source = buffered
        ? { elements: buffered.elements, appState: buffered.appState }
        : { elements: scene.elements, appState: scene.appState ?? undefined };

      const liveAppState = excalidrawAPI.getAppState();
      const restored = restore(
        source,
        {
          // Server scenes omit viewport fields. Preserve the measured canvas so
          // fitting can run; a silent reload also keeps the user's current view.
          width: liveAppState.width,
          height: liveAppState.height,
          offsetTop: liveAppState.offsetTop,
          offsetLeft: liveAppState.offsetLeft,
          ...(isSilentReload
            ? {
                scrollX: liveAppState.scrollX,
                scrollY: liveAppState.scrollY,
              }
            : {}),
        },
        null,
        { repairBindings: true },
      );
      if (isSilentReload) {
        // restoreAppState intentionally defaults zoom when server data omits
        // it, so restore the live zoom explicitly for a background refresh.
        restored.appState.zoom = liveAppState.zoom;
      }
      // Mark the freshly-loaded server scene as the in-sync baseline BEFORE
      // applying it, so the resulting onChange echo doesn't autosave. A restored
      // buffer is intentionally NOT marked synced, so its echo flushes it.
      if (!buffered) {
        // The version the scene was read at — a reload may return a newer one
        // than the probe/409 that triggered it.
        cloudMarkSynced(restored.elements, restored.appState, file?.version);
      }
      // This file's scene is now the editor's scene: autosave may write to it.
      // Before this point every change belonged to the PREVIOUS scene (the
      // scratch canvas the editor mounted with) and saving it would have
      // overwritten the file — so the gate is opened here, and only here, on
      // both the server and restored-buffer paths.
      cloudMarkHydrated();
      if (!isSilentReload) {
        stopFitting = fitLoadedScene(
          excalidrawAPI,
          restored.elements,
          false,
          () => {
            if (!cancelled) {
              setCloudSceneReady(true);
            }
          },
        );
      }
      excalidrawAPI.updateScene({ ...restored, commitToHistory: false });
      // Success: record the id so a token refresh doesn't re-hydrate over live
      // edits. Fresh loads keep their cover until the fitted viewport paints;
      // silent reloads never raised it in the first place.
      hydratedFileIdRef.current = cloudFileId;
      if (isSilentReload) {
        setCloudSceneReady(true);
      }

      const fileIds = restored.elements
        .filter(isInitializedImageElement)
        .map((el) => el.fileId);
      if (fileIds.length) {
        // Token required: private-file assets are owner-gated at /api/assets.
        await loadCloudImages(
          excalidrawAPI,
          cloudFileId,
          fileIds,
          session?.access_token,
          () => cancelled,
        );
      }
    })();
    return () => {
      cancelled = true;
      stopFitting?.();
    };
  }, [
    cloudFileId,
    excalidrawAPI,
    session?.user?.id,
    session?.access_token,
    hydrationRetry,
    cloudMarkSynced,
    cloudMarkHydrated,
    loadCloudImages,
  ]);

  // Re-attempt image assets that failed under the previous token. The hydration
  // effect above bails for an already-hydrated file, so a token arriving late
  // (or refreshing) would otherwise never fix its broken images.
  useEffect(() => {
    const pending = pendingCloudImagesRef.current;
    if (
      !excalidrawAPI ||
      !pending ||
      !shouldRetryCloudImages(pending, cloudFileId, session?.access_token)
    ) {
      return;
    }
    let cancelled = false;
    void loadCloudImages(
      excalidrawAPI,
      pending.fileId,
      pending.fileIds,
      session?.access_token,
      () => cancelled,
    );
    return () => {
      cancelled = true;
    };
  }, [cloudFileId, excalidrawAPI, session?.access_token, loadCloudImages]);

  const handleRenameFile = useCallback(
    (title: string) => {
      if (!cloudFile) {
        return;
      }
      const previous = cloudFile;
      // Optimistic: reflect the new title immediately, revert on failure.
      setCloudFile({ ...previous, title });
      renameFile(previous.id, title).catch((error) => {
        console.error(error);
        setCloudFile(previous);
      });
    },
    [cloudFile],
  );

  const handleSetVisibility = useCallback(
    (next: FileVisibility) => {
      if (!cloudFile) {
        return Promise.resolve();
      }
      const previous = cloudFile;
      // Optimistic: reflect the new visibility immediately; revert on failure so
      // the Share popover (which mirrors this state) also snaps back.
      setCloudFile({ ...previous, visibility: next });
      return setVisibility(previous.id, next)
        .then((updated) => {
          setCloudFile(updated);
        })
        .catch((error) => {
          console.error(error);
          setCloudFile(previous);
          throw error;
        });
    },
    [cloudFile],
  );

  // Actually start the new mindmap: fresh editor with no DB row — the row +
  // first save happen on the first real edit (create-on-first-edit). This RESETS
  // the scene, so every caller must have established that nothing is being lost.
  const startNewMindmapNow = useCallback(
    (options?: { discardPending?: boolean }) => {
      cloudStartNewMindmap(options);
      setCloudFile(null);
      setCloudNotFound(false);
      setNewMindmapPrompt(null);
      excalidrawAPI?.resetScene();
      setHasSceneContent(false);
      // resetScene() only blanks the editor; the discarded scene still lives in
      // the anonymous scratch localStorage keys and would resurrect on reload or
      // on the next focus/visibility sync. Wipe them (and reset the tab-sync
      // versions so the sync can't treat the wipe as "newer") so discard sticks.
      hydratedFileIdRef.current = null;
      LocalData.clearScratchScene();
      // Only when we're actually leaving a file URL: pushing "/" onto "/" adds a
      // duplicate entry, so the next Back press appears to do nothing.
      if (window.location.pathname !== "/" || window.location.search) {
        window.history.pushState({}, "", "/");
      }
      setLocationTick((tick) => tick + 1);
    },
    [cloudStartNewMindmap, excalidrawAPI],
  );

  // ☰ → New mindmap. Guarded: this used to wipe an unsaved canvas instantly,
  // with no confirm and no undo (the scratch canvas lives only in localStorage,
  // which the reset then overwrites).
  const handleNewMindmap = useCallback(() => {
    // With a cloud file open the scratch canvas is left untouched in
    // localStorage (onChange returns before LocalData.save in a cloud context),
    // yet startNewMindmapNow calls clearScratchScene(). Read the stored scratch
    // scene so the confirm also fires when that browser-only work would be lost
    // (finding #6). A scene with only deleted elements counts as empty.
    const scratchHasContent = importFromLocalStorage().elements.some(
      (element) => !element.isDeleted,
    );
    const state = {
      hasContent: hasSceneContent,
      isCloudContext,
      hasUnsavedCloudChanges: cloudHasUnsavedChanges,
      scratchHasContent,
    };
    if (decideNewMindmap(state) === "confirm") {
      // The kind decides the copy AND whether a "Save it first" button is honest.
      setNewMindmapPrompt(unsavedWorkKind(state));
      return;
    }
    startNewMindmapNow();
  }, [
    cloudHasUnsavedChanges,
    hasSceneContent,
    isCloudContext,
    startNewMindmapNow,
  ]);

  // My Files → New mindmap arrives as `/?new=1` (see data/cloudRouting): the two
  // pages are separate documents, so the request travels in the URL. It must do
  // exactly what ☰ → New mindmap does, guard included — the scratch canvas is
  // browser-only work that a reset destroys. The guard reads the STORED scratch
  // scene rather than `hasSceneContent`, which is only set once the editor has
  // loaded and would race this effect.
  useEffect(() => {
    if (!excalidrawAPI || !newMindmapRequestRef.current) {
      return;
    }
    newMindmapRequestRef.current = false;
    // Scrub the marker (replace, not push) so a reload or Back can't reset the
    // canvas a second time.
    window.history.replaceState({}, "", "/");
    const scratchHasContent = importFromLocalStorage().elements.some(
      (element) => !element.isDeleted,
    );
    if (scratchHasContent) {
      setNewMindmapPrompt("scratch");
      return;
    }
    startNewMindmapNow();
  }, [excalidrawAPI, startNewMindmapNow]);

  // Signed in = always a file: landing on `/` with an EMPTY canvas enters the
  // same create-on-first-edit state as the other New-mindmap entry points, so
  // the first stroke creates the file. Unlike those, this must NOT reset the
  // scene or touch the URL (nothing is being replaced), so it drives the
  // lifecycle directly instead of going through startNewMindmapNow. One-shot
  // per visit to `/`: once entered (or once the user has scratch work), it is
  // not re-evaluated until a file is opened, which retires the shot — coming
  // back to `/` afterwards (browser Back, My Files → new) is a new visit to the
  // scratch canvas, and being a one-shot per MOUNT meant the rule quietly
  // stopped applying for the rest of the session.
  const enteredCreateOnFirstEditRef = useRef(false);
  useEffect(() => {
    if (cloudFileId) {
      enteredCreateOnFirstEditRef.current = false;
      return;
    }
    if (enteredCreateOnFirstEditRef.current) {
      return;
    }
    if (
      !shouldEnterCreateOnFirstEdit({
        hasToken: !!session?.access_token,
        authResolved,
        editorReady: !!excalidrawAPI,
        isCloudContext,
        // A `#room=` session or a `#json=` import owns the canvas; only a bare
        // `/` is the scratch canvas this rule is about.
        isScratchCanvas:
          window.location.pathname === "/" && !window.location.hash,
        scratchHasContent: importFromLocalStorage().elements.some(
          (element) => !element.isDeleted,
        ),
      })
    ) {
      return;
    }
    enteredCreateOnFirstEditRef.current = true;
    cloudStartNewMindmap();
  }, [
    session?.access_token,
    authResolved,
    excalidrawAPI,
    isCloudContext,
    cloudFileId,
    cloudStartNewMindmap,
  ]);

  // Stable handlers for the memo'd AppMainMenu: inline arrows would defeat the
  // memo, and every extra menu render churns the tunnel-rat store — the
  // amplifier in the React #185 loop (see the guard in onChange below).
  const openSaveToCloud = useCallback(() => setIsSaveToCloudOpen(true), []);
  const openShareModal = useCallback(() => setShareModalOpen(true), []);

  // "Save it first" in that confirmation.
  const handleSaveThenNew = useCallback(async () => {
    if (isPreCreate) {
      // No row yet, so "save" means CREATE — and it has to finish before the
      // reset, which blanks the editor AND clears the scratch copy. Firing it
      // un-awaited destroyed the very work the button promised to save; a
      // failure now leaves the canvas alone and keeps the prompt honest.
      if (!(await cloudSaveNow())) {
        return;
      }
      startNewMindmapNow();
      return;
    }
    if (isCloudContext) {
      // An open cloud file: push the pending edit now (the default, non-
      // discarding path flushes) and move on.
      startNewMindmapNow();
      return;
    }
    // Scratch canvas: run the existing Save-to-My-Files flow, then start new
    // once it has actually landed.
    startNewAfterSaveRef.current = true;
    setNewMindmapPrompt(null);
    setIsSaveToCloudOpen(true);
  }, [isCloudContext, isPreCreate, cloudSaveNow, startNewMindmapNow]);

  // Only ever called by a CONFIRMED save (the dialog reports failures itself and
  // rolls its row back), which is what makes clearing the scratch scene safe
  // here: a cancelled or failed save never reaches this.
  const handleSavedToCloud = useCallback(
    (fileId: string) => {
      setIsSaveToCloudOpen(false);
      setShowSaveWorkPrompt(false);
      // The scratch canvas has just been promoted into a file, so its
      // localStorage copy is a duplicate that `/` would keep showing (and then
      // diverge from the file). Saving a copy of an OPEN cloud file says nothing
      // about the scratch scene, so leave it alone there.
      if (!isCloudContext) {
        LocalData.clearScratchScene();
      }
      if (startNewAfterSaveRef.current) {
        // The save was only a prerequisite for "New mindmap": the work is now
        // in My Files, so hand the user the blank canvas they asked for rather
        // than opening the file we just wrote.
        startNewAfterSaveRef.current = false;
        startNewMindmapNow();
        return;
      }
      adoptFileAlreadyInEditor(fileId);
    },
    [adoptFileAlreadyInEditor, isCloudContext, startNewMindmapNow],
  );

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();
      // Synchronously buffer the newest cloud edit — even if a save is in flight
      // (flushNow alone no-ops then) — and learn whether cloud work is unsaved.
      const cloudUnsaved = cloudFlushBeforeUnload();

      if (
        cloudUnsaved ||
        (excalidrawAPI &&
          LocalData.fileStorage.shouldPreventUnload(
            excalidrawAPI.getSceneElements(),
          ))
      ) {
        preventUnload(event);
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI, cloudFlushBeforeUnload]);

  // Ctrl/Cmd+S = cloud save, not the OS "save to disk" dialog. Core's
  // actionSaveToActiveFile matches plain Ctrl/Cmd+S and, with no file handle,
  // falls through to a disk export. We intercept in the CAPTURE phase (core
  // listens on `document` in the bubble phase) and stop it — but only for a
  // signed-in user; signed out, core's disk-save behaviour is left untouched.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isSave =
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        (event.key === "s" || event.key === "S");
      if (!isSave || !session?.access_token) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isCloudContext) {
        // Open cloud file: push the pending edit now; the status chip is the
        // saved feedback.
        cloudFlushNow();
      } else if (hasSceneContent) {
        // Scratch canvas: same as the Save button — save into My Files.
        setIsSaveToCloudOpen(true);
      }
    };
    document.addEventListener(EVENT.KEYDOWN, onKeyDown, true);
    return () => document.removeEventListener(EVENT.KEYDOWN, onKeyDown, true);
  }, [session?.access_token, isCloudContext, hasSceneContent, cloudFlushNow]);

  // Seed the content flag from whatever the editor was initialised with (a
  // restored scratch canvas fires no onChange until the user touches it).
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    setHasSceneContent(
      excalidrawAPI.getSceneElements().some((element) => !element.isDeleted),
    );
  }, [excalidrawAPI]);

  // Login handoff: once signed in with non-empty scratch work and no cloud file
  // open, offer to save it. Session-scoped dismissal — no nagging on remount.
  useEffect(() => {
    // Depends on `hasSceneContent` (not a live read) so drawing AFTER signing in
    // on an empty canvas still offers the prompt — the login instant is not the
    // only moment content can first appear.
    if (
      shouldOfferSaveWorkPrompt({
        hasToken: !!session?.access_token,
        authResolved,
        isCloudContext,
        dismissed: saveWorkPromptDismissedRef.current,
        hasContent: hasSceneContent,
      })
    ) {
      setShowSaveWorkPrompt(true);
    }
  }, [session?.access_token, authResolved, isCloudContext, hasSceneContent]);

  useEffect(() => {
    languageDetector.cacheUserLanguage(langCode);
  }, [langCode]);

  const [theme, setTheme] = useState<Theme>(
    () =>
      (localStorage.getItem(
        STORAGE_KEYS.LOCAL_STORAGE_THEME,
      ) as Theme | null) ||
      // FIXME migration from old LS scheme. Can be removed later. #5660
      importFromLocalStorage().appState?.theme ||
      THEME.LIGHT,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_THEME, theme);
    // currently only used for body styling during init (see public/index.html),
    // but may change in the future
    document.documentElement.classList.toggle("dark", theme === THEME.DARK);
  }, [theme]);

  // Remember pills the user deletes, so a later refresh does not resurrect them
  // (tombstones alone are stripped by every save). onChange is the seam because
  // deletion has no single gesture — Delete key, context menu, cut all land here
  // — but the recorder defers its scene write out of the commit phase and does
  // nothing at all when no map node was deleted; see its definition.
  const recordMindmapDeletions = useMemo(
    () => createMindmapDeletionRecorder(() => excalidrawAPI),
    [excalidrawAPI],
  );

  const onChange = (
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    recordMindmapDeletions(elements);

    // This runs from Excalidraw's componentDidUpdate (commit phase), so both
    // setStates below MUST be guarded. React's eager same-value bail-out only
    // applies when the fiber has no pending lanes; during an SPA navigation it
    // always does, so an unconditional setState here re-enters the commit and
    // blows the nested-update limit (React #185).
    if (theme !== appState.theme) {
      setTheme(appState.theme);
    }

    // Is there anything on the canvas? Gates the top-right Save button and the
    // unsaved-work guard in front of New mindmap.
    const nextHasSceneContent = elements.some((element) => !element.isDeleted);
    if (hasSceneContent !== nextHasSceneContent) {
      setHasSceneContent(nextHasSceneContent);
    }

    // Cloud file (open / pending-new / creating): route to the per-file buffer
    // + cloud autosave. Crucially, do NOT fall through to `LocalData.save`,
    // which would write the anonymous scratch keys and clobber scratch work.
    if (isCloudContext) {
      cloudHandleChange(elements, appState);
      // ...EXCEPT before the row exists. In `pendingNew` there is no fileId, so
      // the offline buffer (keyed by fileId) cannot hold this scene either: from
      // the first stroke until the create PUT lands, the work would live only in
      // React state — and a failed create reverts to `pendingNew`, so an offline
      // user stays there indefinitely. Keep writing the scratch keys until the
      // file is real. Nothing is clobbered: create-on-first-edit only starts on
      // an EMPTY scratch canvas, and New mindmap clears it.
      if (!isPreCreate) {
        return;
      }
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
            });
          }
        }
      });
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const onCollabDialogOpen = useCallback(
    () => setCollabDialogOpen(true),
    [setCollabDialogOpen],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  // Opening a cloud file: keep a neutral loading state until auth resolves, so
  // an owner's private file never flashes 404 before the session token arrives.
  if (cloudFileId && !authResolved) {
    return (
      <div
        data-testid="cloud-file-loading"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
        }}
      >
        Loading…
      </div>
    );
  }

  // Cloud file the viewer can't open (missing / private / deleted / logged-out):
  // uniform friendly 404, no existence leak.
  if (cloudFileId && cloudNotFound) {
    return <CloudFile404 />;
  }

  // Transient failure reading the file (network / 5xx): this is NOT a 404 — the
  // owner's file exists, the server just blipped. Offer a retry rather than lie.
  if (cloudFileId && cloudError) {
    return (
      <div
        data-testid="cloud-file-error"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          height: "100%",
          color: "#374151",
        }}
      >
        <div>Couldn’t load this mindmap. Please try again.</div>
        <button
          type="button"
          onClick={() => {
            setCloudError(false);
            setHydrationRetry((tick) => tick + 1);
          }}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "0.5rem",
            border: "1px solid #d1d5db",
            background: "#f9fafb",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const handleCloseBanner = (e: any) => {
    e.preventDefault();
    setShowBanner(false);
  };

  // Something to save, that isn't saved: a signed-in user on a scratch canvas
  // with content. Once the file is in the cloud, autosave owns it and the
  // title + status chips replace this button.
  const canSaveToCloud =
    !!session?.access_token && !isCloudContext && hasSceneContent;

  // Share is offered for an open cloud file, and to a signed-in user on the
  // scratch canvas (where the popover prompts them to save first — hiding it
  // there is what made the whole share feature undiscoverable).
  const canShare =
    (!!cloudFileId && !!cloudFile) || (!cloudFileId && !!session?.access_token);

  // Collab availability, hoisted out of renderTopRightUI so the mobile share
  // modal (rendered outside that callback) can offer "Live collaborate" too.
  const collabReadyOuter = !!collabAPI && !isCollabDisabled;

  return (
    <div
      style={{ height: showBanner ? "93%" : "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      {/* Cloud file still loading: the editor must stay mounted (hydration needs
          its imperative API), so cover the empty board with a loading overlay
          until the scene is applied — no empty-then-pop flash. */}
      {cloudFileId && !cloudNotFound && !cloudError && !cloudSceneReady && (
        <div
          data-testid="cloud-file-loading"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            background: "var(--island-bg-color, #fff)",
            color: "#6b7280",
          }}
        >
          Loading…
        </div>
      )}
      {showBanner && (
        <div className="banner">
          <>
            <a
              href="https://www.producthunt.com/posts/mindmap-so?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-mindmap&#0045;so"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: ".5rem",
              }}
            >
              <h1 style={{ color: "white", fontSize: "1.1rem" }}>
                We are live on Producthunt 🚀 Show us some love 👉
              </h1>
              <div>
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=488754&theme=light"
                  alt="Mindmap&#0046;so - Visualize&#0032;notion&#0032;pages&#0032;in&#0032;whiteboard | Product Hunt"
                  style={{ width: "250px", height: "34px" }}
                  width="250"
                  height="54"
                />
              </div>
            </a>
          </>
          <XMarkIcon
            style={{
              width: "20px",
              height: "20px",
              color: "white",
              cursor: "pointer",
              textAlign: "right",
              position: "absolute",
              top: "1rem",
              right: "1rem",
              margin: "0 1rem",
            }}
            onClick={handleCloseBanner}
            className="close-button"
          />
        </div>
      )}
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            saveAsImage: true,
            export: {
              // No onExportToBackend: the legacy "create a shareable snapshot
              // link" flow is retired (issue #37) — omitting this prop is
              // what makes the core export dialog's link card not render.
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <></>
                      // <ExportToExcalidrawPlus
                      //   elements={elements}
                      //   appState={appState}
                      //   files={files}
                      //   name={excalidrawAPI.getName()}
                      //   onError={(error) => {
                      //     excalidrawAPI?.updateScene({
                      //       appState: {
                      //         errorMessage: error.message,
                      //       },
                      //     });
                      //   }}
                      //   onSuccess={() => {
                      //     excalidrawAPI.updateScene({
                      //       appState: { openDialog: null },
                      //     });
                      //   }}
                      // />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={theme}
        renderTopRightUI={(isMobile) => {
          if (isMobile) {
            return null;
          }
          const collabReady = !!collabAPI && !isCollabDisabled;
          if (!collabReady && !canSaveToCloud && !canShare) {
            return null;
          }
          return (
            <div className="top-right-ui">
              {/* Primary save affordance, next to Share — not buried in the ☰
                  menu beside Excalidraw's unrelated "Save to..." export. */}
              {canSaveToCloud && (
                <SaveToCloudButton
                  onSelect={() => setIsSaveToCloudOpen(true)}
                />
              )}
              {/* The one Share affordance. Its popover already contains Live
                  collaborate, so there is no separate collab "Share" button:
                  two buttons both labelled Share, side by side, was the whole
                  problem. It renders inline here rather than floating over this
                  row, which is what made it overlap Logout. */}
              {canShare && (
                <ShareControl
                  fileId={cloudFile?.id}
                  visibility={cloudFile?.visibility}
                  onSetVisibility={handleSetVisibility}
                  onCollabDialogOpen={
                    collabReady ? onCollabDialogOpen : undefined
                  }
                  // Share replaces LiveCollaborationTrigger for signed-in
                  // users, so it has to carry the "session is live" signal.
                  isCollaborating={isCollaborating}
                  onSaveRequest={
                    cloudFile ? undefined : () => setIsSaveToCloudOpen(true)
                  }
                />
              )}
              {/* Collab is inside the Share popover, so this standalone trigger
                  would be a second button also labelled Share. Keep it only for
                  users who get no Share popover (signed out) — otherwise they'd
                  have no visible way into a collab session at all. */}
              {collabReady && !canShare && (
                <LiveCollaborationTrigger
                  isCollaborating={isCollaborating}
                  onSelect={onCollabDialogOpen}
                />
              )}
              {collabReady && <OAuthIntegration />}
              {collabReady && collabError.message && (
                <CollabError collabError={collabError} />
              )}
            </div>
          );
        }}
        renderIntegration={RenderIntegration}
        renderCommandPalette={CommandPalette}
        // onPointerDown={() => setShow(false)}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          showSaveToCloud={
            !!session?.access_token && !cloudAutosave.isCloudContext
          }
          onSaveToCloud={openSaveToCloud}
          showNewMindmap={!!session?.access_token}
          onNewMindmap={handleNewMindmap}
          showShare={canShare}
          onShareOpen={openShareModal}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          {/* {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )} */}
        </OverwriteConfirmDialog>
        <AppFooter />
        <TTDDialog
          onTextSubmit={async (input) => {
            try {
              const response = await fetch(
                `${
                  import.meta.env.VITE_APP_AI_BACKEND
                }/v1/ai/text-to-diagram/generate`,
                {
                  method: "POST",
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ prompt: input }),
                },
              );

              const rateLimit = response.headers.has("X-Ratelimit-Limit")
                ? parseInt(response.headers.get("X-Ratelimit-Limit") || "0", 10)
                : undefined;

              const rateLimitRemaining = response.headers.has(
                "X-Ratelimit-Remaining",
              )
                ? parseInt(
                    response.headers.get("X-Ratelimit-Remaining") || "0",
                    10,
                  )
                : undefined;

              const json = await response.json();

              if (!response.ok) {
                if (response.status === 429) {
                  return {
                    rateLimit,
                    rateLimitRemaining,
                    error: new Error(
                      "Too many requests today, please try again tomorrow!",
                    ),
                  };
                }

                throw new Error(json.message || "Generation failed...");
              }

              const generatedResponse = json.generatedResponse;
              if (!generatedResponse) {
                throw new Error("Generation failed...");
              }

              return { generatedResponse, rateLimit, rateLimitRemaining };
            } catch (err: any) {
              throw new Error("Request failed");
            }
          }}
        />
        <TTDDialogTrigger />
        <ExtraToolsSlot>
          <AgentsToolbarItem />
        </ExtraToolsSlot>
        <AgentsSidebar />
        {isCollaborating && isOffline && (
          <div className="collab-offline-warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <CollabDialog collabAPI={collabAPI} />

        {cloudFileId && cloudFile && (
          <FileTitleChip title={cloudFile.title} onRename={handleRenameFile} />
        )}

        {cloudAutosave.isCloudContext && (
          <SaveStatusChip
            status={cloudAutosave.status}
            savedAt={cloudAutosave.savedAt}
            hasConflict={cloudAutosave.hasConflict}
            imageWarning={cloudAutosave.imageWarning}
            errorMessage={cloudAutosave.errorMessage}
            hasUnsavedChanges={cloudAutosave.hasUnsavedChanges}
            onSaveNow={cloudFlushNow}
          />
        )}

        {cloudAutosave.conflict && (
          <VersionConflictBanner
            onReload={() => cloudAutosave.resolveConflict("reload")}
            onKeepMine={() => cloudAutosave.resolveConflict("keep")}
          />
        )}

        {showSaveWorkPrompt && (
          <SaveWorkPrompt
            onAccept={() => {
              // Accepting is also a dismissal: don't re-offer (e.g. on the next
              // token refresh) if the user then backs out of the save dialog.
              saveWorkPromptDismissedRef.current = true;
              setShowSaveWorkPrompt(false);
              setIsSaveToCloudOpen(true);
            }}
            onDismiss={() => {
              saveWorkPromptDismissedRef.current = true;
              setShowSaveWorkPrompt(false);
            }}
          />
        )}

        {newMindmapPrompt && (
          <NewMindmapConfirmDialog
            kind={newMindmapPrompt}
            fileTitle={cloudFile?.title}
            // No save rescues a scratch scene sitting behind a synced cloud
            // file (Save-to-cloud would save the on-screen cloud file), so
            // offering the button there would be a lie followed by data loss.
            onSaveFirst={
              session?.access_token &&
              newMindmapPrompt !== "scratchBehindCloudFile"
                ? handleSaveThenNew
                : undefined
            }
            onDiscard={() => startNewMindmapNow({ discardPending: true })}
            onCancel={() => setNewMindmapPrompt(null)}
          />
        )}

        {isSaveToCloudOpen && excalidrawAPI && (
          <SaveToCloudDialog
            excalidrawAPI={excalidrawAPI}
            accessToken={session?.access_token}
            onClose={() => {
              // Backing out of the save also backs out of the new mindmap it
              // was a prerequisite for — never reset the canvas unasked.
              startNewAfterSaveRef.current = false;
              setIsSaveToCloudOpen(false);
            }}
            onSaved={handleSavedToCloud}
          />
        )}

        {/* Share popover as a centered modal — the entry point for surfaces
            with no top-right anchor (mobile), opened from the ☰ Share… item.
            Same SharePopover content as the desktop anchored button (reused,
            not forked). */}
        {shareModalOpen && canShare && (
          <div
            data-testid="share-modal-backdrop"
            role="presentation"
            onClick={() => setShareModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
              background: "rgba(0,0,0,0.35)",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: "relative", maxWidth: "22rem", width: "100%" }}
            >
              <button
                type="button"
                aria-label="Close share"
                data-testid="share-modal-close"
                onClick={() => setShareModalOpen(false)}
                style={{
                  position: "absolute",
                  top: "-0.6rem",
                  right: "-0.6rem",
                  zIndex: 1,
                  width: "1.75rem",
                  height: "1.75rem",
                  borderRadius: "50%",
                  border: "none",
                  background: "var(--island-bg-color)",
                  color: "var(--text-primary-color)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  cursor: "pointer",
                  fontSize: "1rem",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
              <SharePopoverWithEmbed
                fileId={cloudFile?.id}
                visibility={cloudFile?.visibility}
                onSetVisibility={handleSetVisibility}
                onCollabDialogOpen={
                  collabReadyOuter
                    ? () => {
                        setShareModalOpen(false);
                        onCollabDialogOpen();
                      }
                    : undefined
                }
                isCollaborating={isCollaborating}
                onSaveRequest={
                  cloudFile
                    ? undefined
                    : () => {
                        setShareModalOpen(false);
                        setIsSaveToCloudOpen(true);
                      }
                }
              />
            </div>
          </div>
        )}

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  const { session, feedbackSubmitted, markFeedbackSubmitted } = useAuth();

  return (
    <TopErrorBoundary>
      <Provider unstable_createStore={() => appJotaiStore}>
        <ExcalidrawWrapper />
        <NotionSidePeek />

        {/* Feedback Modal - shows after Notion authentication if not yet submitted */}
        {/* Only show when feedbackSubmitted is explicitly false (not null/loading) */}
        {isLegacyFeedbackModalOpen({ session, feedbackSubmitted }) && (
          <FeedbackModal isOpen={true} onSuccess={markFeedbackSubmitted} />
        )}
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
